import { createSelector } from '@ngrx/store';

import { Ability } from '@core/abilities-types';
import { AccountSelectors } from '@core/account-data';
import { createActionsWithReducer, memoizeSelector, ngrxEvent } from '@core/ngrx-legacy';

const ALL_ABILITIES = <Record<Ability, false>>(
  Object.fromEntries(Object.values(Ability).map(ability => [ability, false]))
);

const { actions } = createActionsWithReducer('Abilities', null, {
  roleUpdated: ngrxEvent<void>(),
  membershipUpdated: ngrxEvent<void>(),
});

const abilitiesMapSelector = createSelector(
  AccountSelectors.currentCompany,
  function abilitiesMap(company): Record<Ability, boolean> {
    return company
      ? {
          ...ALL_ABILITIES,
          ...Object.fromEntries(company.abilities.map((ability: Ability) => [ability, true])),
        }
      : ALL_ABILITIES;
  },
);

const hasOneSelector = memoizeSelector((ability: Ability) =>
  createSelector(
    abilitiesMapSelector,
    function abilitiesHasOne(abilities: Record<Ability, boolean>) {
      return abilities[ability];
    },
  ),
);

const hasManySelector = memoizeSelector((abilities: Ability[]) =>
  createSelector(
    abilitiesMapSelector,
    function abilitiesHasMany(abilitiesMap: Record<Ability, boolean>) {
      return abilities.every(ability => abilitiesMap[ability]);
    },
  ),
);

const hasAnySelector = memoizeSelector((abilities: Ability[]) =>
  createSelector(
    abilitiesMapSelector,
    function abilitiesHasAny(abilitiesMap: Record<Ability, boolean>) {
      return abilities.some(ability => abilitiesMap[ability]);
    },
  ),
);

const isAdminInCurrentCompanySelector = createSelector(
  AccountSelectors.currentCompany,
  function isAdminInCurrentCompany(company) {
    return !!company && company.isAdmin;
  },
);

export { actions as AbilitiesActions };
export const AbilitiesSelectors = {
  abilityMap: abilitiesMapSelector,
  hasOne: hasOneSelector,
  hasMany: hasManySelector,
  hasAny: hasAnySelector,
  isAdminInCurrentCompany: isAdminInCurrentCompanySelector,
};
